import { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import type { WidgetCompatibilityWarning } from '@/lib/types';

interface CompatibilityWarningBannerProps {
  warnings: WidgetCompatibilityWarning[];
}

export function CompatibilityWarningBanner({ warnings }: CompatibilityWarningBannerProps) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const warning = warnings[0] ?? null;

  if (!warning || dismissed) {
    return null;
  }

  return (
    <div
      data-testid="compatibility-warning-banner"
      data-compatibility-state="partial"
      role="note"
      aria-live="polite"
      aria-atomic="true"
      className="mb-2 rounded-md border border-amber-300/80 bg-amber-50 px-3 py-2 text-amber-950 dark:border-amber-600/50 dark:bg-amber-500/10 dark:text-amber-100"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-xs font-medium leading-5 break-words">{warning.reason}</p>
          {warning.fixCta ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="min-h-[44px] border border-amber-300/70 bg-white/80 text-amber-950 hover:bg-white dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100"
              data-testid="compatibility-fix-cta"
              onClick={() => navigate(warning.fixCta!.path)}
            >
              {warning.fixCta.label}
            </Button>
          ) : null}
        </div>
        <IconButton
          type="button"
          variant="ghost"
          size="icon"
          className="h-11 w-11 shrink-0 text-amber-950 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-500/10"
          tooltip="Dismiss compatibility warning"
          onClick={() => setDismissed(true)}
        >
          <X className="h-4 w-4" />
        </IconButton>
      </div>
    </div>
  );
}

export default CompatibilityWarningBanner;